import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Calendar, CheckCircle2, AlertTriangle, ChevronRight } from "lucide-react";
import { api, formatZAR, formatDate, MONTHS } from "../api";

const REPEAT_OPTIONS = [
  { k: "monthly", label: "Monthly only", color: "#00ACB3" },
  { k: "annual", label: "Annual only", color: "#0F4F51" },
  { k: "recurring", label: "Monthly + Annual", color: "#0F4F51" },
  { k: "all", label: "All tasks", color: "#595959" },
];

export default function Recurring() {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get("client_id") || "";
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(null);
  const [cats, setCats] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [range, setRange] = useState("6m");
  const [back, setBack] = useState(6);
  const [fwd, setFwd] = useState(3);
  const [repeatFilter, setRepeatFilter] = useState("recurring");

  useEffect(() => { api.get("/clients").then(r => setClients(r.data)); }, []);
  useEffect(() => { api.get("/task-categories").then(r => setCats(r.data)); }, []);
  useEffect(() => { if (clientId) api.get(`/clients/${clientId}`).then(r => setClient(r.data)); }, [clientId]);

  const applyRange = (r) => {
    setRange(r);
    if (r === "1m") { setBack(1); setFwd(0); }
    else if (r === "3m") { setBack(3); setFwd(0); }
    else if (r === "6m") { setBack(6); setFwd(0); }
    else if (r === "12m") { setBack(12); setFwd(3); }
    // 'custom' keeps current back/fwd
  };

  // Compute date range based on back/fwd
  const periods = useMemo(() => {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth() + 1;
    const out = [];
    for (let d = -back + 1; d <= fwd; d++) {
      const idx = y * 12 + (m - 1) + d;
      out.push({ year: Math.floor(idx / 12), month: (idx % 12) + 1 });
    }
    // Newest first (descending)
    return out.reverse();
  }, [back, fwd]);

  const loadTasks = async () => {
    if (!clientId) { setTasks([]); return; }
    // Fetch tasks for the full date range via two cheap calls per period would be slow;
    // simpler: fetch all client tasks and filter client-side.
    const r = await api.get("/tasks", { params: { client_id: clientId } });
    setTasks(r.data);
  };
  useEffect(() => { loadTasks(); }, [clientId]);

  const catFor = (id) => cats.find(c => c.id === id);
  const setParam = (k, v) => {
    const np = new URLSearchParams(searchParams);
    if (v) np.set(k, v); else np.delete(k);
    setSearchParams(np);
  };

  // Filter + group tasks by period
  const grouped = useMemo(() => {
    const filtered = tasks.filter(t => {
      if (repeatFilter === "all") return true;
      if (repeatFilter === "recurring") return t.repeat_type === "monthly" || t.repeat_type === "annual";
      return t.repeat_type === repeatFilter;
    });
    const byPeriod = {};
    for (const p of periods) {
      const key = `${p.year}-${String(p.month).padStart(2, "0")}`;
      byPeriod[key] = { ...p, key, tasks: [] };
    }
    for (const t of filtered) {
      const key = t.date.slice(0, 7);
      if (byPeriod[key]) byPeriod[key].tasks.push(t);
    }
    return Object.values(byPeriod).map(p => ({
      ...p,
      tasks: [...p.tasks].sort((a, b) => a.date.localeCompare(b.date)),
      total: p.tasks.reduce((s, t) => s + Number(t.price || 0), 0),
      invoiced_total: p.tasks.filter(t => t.invoice_ref).reduce((s, t) => s + Number(t.price || 0), 0),
    }));
  }, [tasks, periods, repeatFilter]);

  if (!clientId) {
    return (
      <div className="p-6 md:p-10 max-w-2xl mx-auto">
        <h1 className="text-3xl font-extrabold text-[#011617] mb-2" style={{ fontFamily: "Manrope" }}>
          Recurring Tasks Overview
        </h1>
        <p className="text-[#595959] mb-6">Pick a client to see a month-by-month list of their tasks.</p>
        <div className="np-card">
          <select onChange={e => setParam("client_id", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3]" defaultValue="" data-testid="rec-client-select">
            <option value="" disabled>Choose a client...</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.business})</option>)}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="np-label mb-2">Birdseye view</div>
          <h1 className="text-3xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
            {client?.name || "—"}
          </h1>
          <p className="text-[#595959] mt-1 text-sm">
            Month-by-month, newest first. Monthly tasks auto-project 3 months forward when added.
          </p>
        </div>
        <Link to={`/workspace?client_id=${clientId}`} className="text-sm font-semibold text-[#00ACB3] hover:text-[#0F4F51] underline">Open workspace →</Link>
      </div>

      {/* Filters */}
      <div className="np-card mb-6 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="np-label inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Period</span>
          {[
            { k: "1m", label: "Last 1m" },
            { k: "3m", label: "Last 3m" },
            { k: "6m", label: "Last 6m" },
            { k: "12m", label: "Last 12m + Next 3m" },
            { k: "custom", label: "Custom" },
          ].map(opt => (
            <button
              key={opt.k}
              data-testid={`rec-range-${opt.k}`}
              onClick={() => applyRange(opt.k)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                range === opt.k ? "bg-[#00ACB3] text-white" : "bg-white text-[#595959] border border-[#CCCCCC]"
              }`}
            >{opt.label}</button>
          ))}
          {range === "custom" && (
            <span className="ml-2 inline-flex items-center gap-2">
              <span className="np-label">Back</span>
              <input type="number" min="0" max="36" value={back} onChange={e => setBack(Math.max(0, Number(e.target.value)))} className="w-16 px-2 py-1 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3]" />
              <span className="np-label">Forward</span>
              <input type="number" min="0" max="36" value={fwd} onChange={e => setFwd(Math.max(0, Number(e.target.value)))} className="w-16 px-2 py-1 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3]" />
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="np-label">Show</span>
          {REPEAT_OPTIONS.map(opt => (
            <button
              key={opt.k}
              data-testid={`rec-filter-${opt.k}`}
              onClick={() => setRepeatFilter(opt.k)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                repeatFilter === opt.k ? "bg-[#00ACB3] text-white" : "bg-white text-[#595959] border border-[#CCCCCC]"
              }`}
            >{opt.label}</button>
          ))}
        </div>
      </div>

      {/* Month-by-month vertical list */}
      <div className="space-y-4">
        {grouped.map(period => (
          <div key={period.key} className="np-card" data-testid={`rec-period-${period.key}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3 pb-3 border-b border-[#CCCCCC]">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
                  {MONTHS[period.month - 1]} {period.year}
                </h2>
                <span className="text-xs text-[#595959]">{period.tasks.length} task{period.tasks.length === 1 ? "" : "s"}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-[#065F46] font-semibold">Invoiced: {formatZAR(period.invoiced_total)}</span>
                <span className="text-[#92400E] font-semibold">Outstanding: {formatZAR(period.total - period.invoiced_total)}</span>
                <Link to={`/workspace?client_id=${clientId}&year=${period.year}&month=${period.month}`} className="text-[#00ACB3] hover:text-[#0F4F51] font-semibold inline-flex items-center">
                  Open <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            {period.tasks.length === 0 ? (
              <div className="text-sm text-[#92400E] inline-flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> No tasks added for this month
              </div>
            ) : (
              <ul className="divide-y divide-[#CCCCCC]">
                {period.tasks.map(t => {
                  const cat = catFor(t.task_category_id);
                  const invoiced = !!t.invoice_ref;
                  return (
                    <li key={t.id} className="py-2.5 flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex items-center gap-2">
                        <CheckCircle2 className={`w-4 h-4 shrink-0 ${invoiced ? "text-[#065F46]" : "text-[#92400E]"}`} />
                        <div className="min-w-0">
                          <div className="font-semibold text-[#011617] truncate">{cat?.name || "—"}</div>
                          <div className="text-xs text-[#595959]">
                            {formatDate(t.date)} • <span className="uppercase tracking-wider font-bold text-[#0F4F51]">{t.repeat_type}</span>
                            {t.notes ? ` • ${t.notes}` : ""}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-right shrink-0">
                        <span className="font-bold text-[#011617]">{formatZAR(t.price)}</span>
                        {invoiced ? (
                          <span className="text-[10px] font-mono text-[#0F4F51] bg-[#E5FFFF] rounded px-1.5 py-0.5">{t.invoice_ref}</span>
                        ) : (
                          <span className="badge-outstanding">Outstanding</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
